/* eslint-disable react/no-unescaped-entities */
/**
 * /coaching landing — the Healing Trilogy overview.
 * Editorial layout matching /coaching/identity's visual language:
 * royal-purple + gold palette, serif-forward, portrait-anchored hero,
 * generous negative space, gold-underlined key words.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Loader2, Sparkles } from "lucide-react";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { SharedHeader, SharedFooter } from "./CoachingProgramPublic";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/coaching`;

const PALETTE = {
  purple: "#4C1D95",
  purpleSoft: "#7C3AED",
  gold: "#B8924B",
  ink: "#0B0B0F",
  paper: "#FAF8F4",
};

const BIO_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_kissed-lite/artifacts/q1qr4hak_Dr.%20Christy%20Vonta%206.png";

function GoldWord({ children }) {
  return (
    <span style={{ borderBottom: `2px solid ${PALETTE.gold}`, paddingBottom: 1 }}>
      {children}
    </span>
  );
}

const PROGRAM_ORDER = [
  "the-me-i-see",
  "healing-what-you-cannot-feel",
  "she-is",
];
const PROGRAM_META = {
  "the-me-i-see": {
    number: "01",
    tagline: "The Entry",
    line: "Identity healing. Eight weeks to meet the woman underneath the woman they told you to be.",
  },
  "healing-what-you-cannot-feel": {
    number: "02",
    tagline: "The Excavation",
    line: "Twelve weeks in the rooms of the body where grief lives. Not to relive — to release.",
  },
  "she-is": {
    number: "03",
    tagline: "The Arrival",
    line: "Twelve weeks of embodiment. She is no longer becoming. She has arrived.",
  },
};

export default function CoachingLanding() {
  useDocumentMeta({
    title: "Coaching & Consulting · Dr. Christy Vonta",
    description:
      "The Healing Trilogy — three signature coaching programs by Dr. Christy Vonta that guide women from inherited identity to embodied becoming.",
  });

  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/programs/public`);
        const data = r.data.programs || r.data || [];
        // Order per trilogy sequence, fall back to featured
        const sorted = data
          .slice()
          .sort(
            (a, b) =>
              (PROGRAM_ORDER.indexOf(a.slug) === -1 ? 99 : PROGRAM_ORDER.indexOf(a.slug)) -
              (PROGRAM_ORDER.indexOf(b.slug) === -1 ? 99 : PROGRAM_ORDER.indexOf(b.slug))
          );
        if (!cancel) setPrograms(sorted);
      } catch {
        if (!cancel) setPrograms([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  return (
    <div
      className="min-h-screen"
      style={{ background: PALETTE.paper, color: PALETTE.ink, fontFamily: "'Inter', sans-serif" }}
      data-testid="coaching-landing"
    >
      <SharedHeader />

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div
            className="text-[11px] uppercase mb-5"
            style={{ letterSpacing: "0.28em", color: PALETTE.gold, fontWeight: 700 }}
            data-testid="landing-eyebrow"
          >
            The Healing Trilogy —
          </div>
          <h1
            className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02]"
            style={{ color: PALETTE.ink }}
          >
            Three programs.
            <br />
            One <GoldWord>becoming</GoldWord>.
          </h1>
          <p
            className="mt-6 text-lg leading-relaxed max-w-xl"
            style={{ color: "#332C3A" }}
          >
            Dr. Christy Vonta's signature coaching arc — a linear, honest, one-woman-at-a-time
            journey from the identity you were <GoldWord>handed</GoldWord> to the identity you
            were <GoldWord>meant</GoldWord> to live.
          </p>
          <div className="mt-8 flex flex-wrap gap-4 items-center">
            <Link
              to="/coaching/the-me-i-see"
              className="inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm tracking-wide text-white"
              style={{
                background: PALETTE.purple,
                boxShadow: "0 14px 30px -10px rgba(76,29,149,0.45)",
              }}
              data-testid="landing-primary-cta"
            >
              Begin with Program 01 <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/coaching/identity"
              className="inline-flex items-center gap-2 text-sm underline"
              style={{ color: PALETTE.purple }}
              data-testid="landing-identity-link"
            >
              Or take the Identity Assessment first →
            </Link>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div
            className="relative rounded-3xl overflow-hidden"
            style={{ boxShadow: "0 30px 60px -30px rgba(76,29,149,0.35)" }}
          >
            <img
              src={BIO_PORTRAIT}
              alt="Dr. Christy Vonta"
              className="w-full h-[420px] sm:h-[520px] object-cover"
            />
          </div>
          <div
            className="mt-4 rounded-2xl p-6"
            style={{ background: "#fff", border: `1px solid ${PALETTE.gold}` }}
          >
            <p
              className="italic leading-relaxed"
              style={{ fontFamily: "'Playfair Display', serif", fontSize: 15.5, color: PALETTE.ink }}
            >
              "Healing begins with who you BELLieve you are."
            </p>
            <div
              className="mt-3 text-[10px] uppercase"
              style={{ letterSpacing: "0.24em", color: PALETTE.gold, fontWeight: 700 }}
            >
              — Dr. Christy Vonta, PhD, DOL, HHP
            </div>
          </div>
        </div>
      </section>

      {/* THREE PROGRAM CARDS */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <div
          className="text-[11px] uppercase mb-8"
          style={{ letterSpacing: "0.28em", color: PALETTE.gold, fontWeight: 700 }}
        >
          The Trilogy — In Order
        </div>
        {loading ? (
          <div className="flex justify-center py-14">
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: PALETTE.purple }} />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {programs
              .filter((p) => PROGRAM_ORDER.includes(p.slug))
              .map((p) => {
                const meta = PROGRAM_META[p.slug] || {};
                return (
                  <Link
                    key={p.slug}
                    to={`/coaching/${p.slug}`}
                    className="group block rounded-3xl p-8 transition-shadow hover:shadow-lg"
                    style={{
                      background: "#fff",
                      border: `1px solid rgba(184,146,75,0.35)`,
                      boxShadow: "0 10px 30px -20px rgba(76,29,149,0.15)",
                    }}
                    data-testid={`landing-program-card-${p.slug}`}
                  >
                    <div
                      className="text-[10px] uppercase"
                      style={{ letterSpacing: "0.24em", color: PALETTE.gold, fontWeight: 700 }}
                    >
                      Program {meta.number} · {meta.tagline}
                    </div>
                    <h3
                      className="font-serif text-3xl mt-2 leading-tight"
                      style={{ color: PALETTE.ink }}
                    >
                      {p.title}
                    </h3>
                    <p
                      className="mt-3 text-[14.5px] leading-relaxed"
                      style={{ color: "#453f52", minHeight: 84 }}
                    >
                      {meta.line}
                    </p>
                    <div className="mt-5 flex items-center justify-between">
                      <div
                        className="text-xs"
                        style={{ color: PALETTE.purple, fontWeight: 700 }}
                      >
                        {p.duration_weeks} weeks · ${p.price_usd?.toLocaleString?.() || p.price_usd}
                      </div>
                      <span
                        className="inline-flex items-center gap-1 text-xs group-hover:translate-x-1 transition-transform"
                        style={{ color: PALETTE.purple, fontWeight: 700 }}
                      >
                        Explore <ArrowRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </Link>
                );
              })}
          </div>
        )}
      </section>

      {/* ARC BAND */}
      <section
        className="mt-6 py-16"
        style={{ background: PALETTE.purple, color: "#fff" }}
      >
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div
            className="text-[11px] uppercase mb-4"
            style={{ letterSpacing: "0.28em", color: PALETTE.gold, fontWeight: 700 }}
          >
            The Arc
          </div>
          <h2
            className="font-serif text-3xl sm:text-4xl leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            From <GoldOnPurple>the woman you were told to be</GoldOnPurple> →<br />
            through <GoldOnPurple>the grief you refused to feel</GoldOnPurple> →<br />
            into <GoldOnPurple>the woman you were meant to become</GoldOnPurple>.
          </h2>
          <p className="mt-6 text-[15px] opacity-90 max-w-xl mx-auto leading-relaxed">
            The Trilogy is linear by design. Each program builds on the one before. Complete Program
            01 to unlock Program 02. Complete Program 02 to arrive at Program 03.
          </p>
          <Link
            to="/coaching/the-me-i-see"
            className="mt-8 inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm tracking-wide"
            style={{ background: "#fff", color: PALETTE.purple, fontWeight: 700 }}
            data-testid="arc-cta"
          >
            <Sparkles className="w-4 h-4" style={{ color: PALETTE.gold }} /> Begin with Program 01
          </Link>
        </div>
      </section>

      <SharedFooter />
    </div>
  );
}

function GoldOnPurple({ children }) {
  return (
    <span style={{ borderBottom: `2px solid ${PALETTE.gold}`, paddingBottom: 1, color: "#fff" }}>
      {children}
    </span>
  );
}
